// Local-only session logger for ToneGuard rewrites.
//
// Records a small structured event each time a rewrite "completes":
//   • user picks the rewritten version          → rewrite_accepted=true
//   • user keeps the original / dismisses        → rewrite_accepted=false
//
// Schema (production v2 — extended for ToneMirror intelligence):
// {
//   session_id,              // stable per-tab id
//   timestamp,               // ISO 8601 when the rewrite resolved
//   communication_style,     // user's style at time of log (live variable)
//   style_change_date,       // when the user last changed style (ISO 8601)
//   intent,                  // optional intent preset
//   dominant_signal,         // detected tone signal
//   signal_weight,           // 🔴=3, med-high=2, med-low=1, 🟢=0
//   tone_label,              // 🔴 / 🟡 / 🟢
//   rewrite_accepted,        // bool
//   last_drift_shown,        // session_id of last drift_detected surfaced
//
//   // Additions (data layer only, no UI yet):
//   relationship_register,   // READ from chat record only. Never re-derived
//                            // from label. null if chat has no register.
//   secondary_signal,        // second highest signal_weight clause.
//                            // null if single-pass detection.
//   positive_signal_present, // bool. true if any 🟢 signal detected. default false.
//   word_count               // integer. count of words in user's input message.
// }
//
// Stored as a rolling list in localStorage. No analytics POSTs, no remote calls.
// Fails silently on every error path (private mode, full quota, hostile browsers).

const STORAGE_KEY = "toneguard:sessionLog";
const SESSION_ID_KEY = "toneguard:sessionId";
const STYLE_CHANGE_KEY = "toneguard:styleChangeDate";
const LAST_DRIFT_KEY = "toneguard:lastDriftShown";
const MAX_ENTRIES = 500;

// Internal severity weights — matches backend's MEDIUM_RISK_WEIGHTS.
// 🔴 high = 3, 🟡 med-high = 2, 🟡 med-low = 1, 🟢 low = 0.
const HIGH_RISK = new Set([
    "accusatory",
    "dismissive",
    "passive_aggressive",
    "threatening",
    "contemptuous",
]);
const MED_HIGH = new Set(["defensive", "pressuring", "cold"]);
const MED_LOW = new Set(["frustrated", "abrupt"]);
const LOW_RISK = new Set([
    "neutral",
    "constructive",
    "appreciative",
    "apologetic",
    "empathetic",
    "assertive",
    "solution_focused",
]);

const safeRandomId = () => {
    try {
        if (typeof crypto !== "undefined" && crypto.randomUUID) {
            return crypto.randomUUID();
        }
    } catch {
        /* fall through */
    }
    return `s-${Date.now().toString(36)}-${Math.random()
        .toString(36)
        .slice(2, 10)}`;
};

const getSessionId = () => {
    try {
        const existing = window.sessionStorage.getItem(SESSION_ID_KEY);
        if (existing) return existing;
        const fresh = safeRandomId();
        window.sessionStorage.setItem(SESSION_ID_KEY, fresh);
        return fresh;
    } catch {
        return safeRandomId();
    }
};

const weightForSignal = (signal) => {
    if (!signal) return 0;
    if (HIGH_RISK.has(signal)) return 3;
    if (MED_HIGH.has(signal)) return 2;
    if (MED_LOW.has(signal)) return 1;
    if (LOW_RISK.has(signal)) return 0;
    return 0;
};

const labelForRisk = (riskLevel) => {
    if (riskLevel === "risky") return "🔴";
    if (riskLevel === "needs_softening") return "🟡";
    if (riskLevel === "good") return "🟢";
    return "🟢";
};

// ─── Style-change tracking ──────────────────────────────────────────────

/**
 * Mark NOW as the user's most recent style-change date. Called whenever
 * the Communication Style preference is updated. Layers 3 and 4 in the
 * ToneMirror intelligence system count ONLY entries logged after this
 * date, so the system "resets" gracefully on style change.
 */
export function setStyleChangeDate(date = new Date()) {
    try {
        window.localStorage.setItem(
            STYLE_CHANGE_KEY,
            date.toISOString(),
        );
    } catch {
        /* silent */
    }
}

export function getStyleChangeDate() {
    try {
        return window.localStorage.getItem(STYLE_CHANGE_KEY) || null;
    } catch {
        return null;
    }
}

// ─── Drift-shown tracking ───────────────────────────────────────────────

export function markDriftShown(sessionId) {
    try {
        if (!sessionId) return;
        window.localStorage.setItem(LAST_DRIFT_KEY, sessionId);
    } catch {
        /* silent */
    }
}

export function getLastDriftShown() {
    try {
        return window.localStorage.getItem(LAST_DRIFT_KEY) || null;
    } catch {
        return null;
    }
}

// ─── Logging + reading ──────────────────────────────────────────────────

/**
 * Helper: is this signal a 🟢 (positive / low-risk) signal? Used by
 * consumers to compute `positive_signal_present` for the log entry.
 */
export function isPositiveSignal(signal) {
    return !!signal && LOW_RISK.has(signal);
}

const safeWordCount = (text) => {
    if (!text || typeof text !== "string") return 0;
    const trimmed = text.trim();
    if (!trimmed) return 0;
    return trimmed.split(/\s+/).length;
};

/**
 * Append one rewrite-completion record. Fail silently on storage errors.
 *
 * @param {object} params
 * @param {string|null} params.intent
 * @param {string|null} params.dominantSignal
 * @param {string|null} params.riskLevel
 * @param {boolean} params.rewriteAccepted
 * @param {string|null} params.communicationStyle
 * @param {string|null} params.relationshipRegister
 *        READ from the chat record only — never re-derived from any label.
 *        Pass through verbatim. null if the chat record has no register.
 * @param {string|null} params.secondarySignal
 *        Second highest signal_weight clause (multi-pass detection).
 *        null when the analyze response is single-pass.
 * @param {boolean} params.positiveSignalPresent
 *        true if any 🟢 signal was detected in the message. default false.
 * @param {number} params.wordCount
 *        Integer count of words in the user's input message. default 0.
 * @param {string} params.messageText
 *        Convenience — when provided, derives `wordCount` if the caller
 *        didn't supply one. Never written to the log itself.
 */
export function logRewriteSession({
    intent = null,
    dominantSignal = null,
    riskLevel = null,
    rewriteAccepted = false,
    communicationStyle = null,
    relationshipRegister = null,
    secondarySignal = null,
    positiveSignalPresent = false,
    wordCount = null,
    messageText = null,
}) {
    try {
        // Derive word_count from messageText if the caller didn't pass an
        // explicit value (keeps call sites tidy without leaking the raw
        // text into storage).
        const resolvedWordCount =
            Number.isFinite(wordCount) && wordCount !== null
                ? Math.max(0, Math.floor(wordCount))
                : safeWordCount(messageText);
        const entry = {
            session_id: getSessionId(),
            timestamp: new Date().toISOString(),
            communication_style: communicationStyle || null,
            style_change_date: getStyleChangeDate(),
            intent: intent || null,
            dominant_signal: dominantSignal || null,
            signal_weight: weightForSignal(dominantSignal),
            tone_label: labelForRisk(riskLevel),
            rewrite_accepted: !!rewriteAccepted,
            last_drift_shown: getLastDriftShown(),
            // ─── Additions (data layer only) ─────────────────────────────
            relationship_register: relationshipRegister || null,
            secondary_signal: secondarySignal || null,
            positive_signal_present: !!positiveSignalPresent,
            word_count: resolvedWordCount,
        };
        const raw = window.localStorage.getItem(STORAGE_KEY);
        let list = [];
        if (raw) {
            try {
                const parsed = JSON.parse(raw);
                if (Array.isArray(parsed)) list = parsed;
            } catch {
                list = [];
            }
        }
        list.push(entry);
        if (list.length > MAX_ENTRIES) {
            list = list.slice(-MAX_ENTRIES);
        }
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
    } catch {
        /* Private-mode browsers, quota exhaustion, sandbox restrictions. */
    }
}

export function readSessionLog() {
    try {
        const raw = window.localStorage.getItem(STORAGE_KEY);
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
}

/**
 * Entries logged AFTER the most recent style change. Used by Layers 3 + 4
 * which "reset" gracefully on style change per spec.
 */
export function readEligibleSessions() {
    const all = readSessionLog();
    const cutoff = getStyleChangeDate();
    if (!cutoff) return all;
    return all.filter((e) => (e.timestamp || "") >= cutoff);
}

/**
 * Erase the log + session id + drift tracker. Wired into the
 * Profile panel's "Reset device data" flow.
 */
export function clearSessionLog() {
    try {
        window.localStorage.removeItem(STORAGE_KEY);
        window.localStorage.removeItem(STYLE_CHANGE_KEY);
        window.localStorage.removeItem(LAST_DRIFT_KEY);
        window.sessionStorage.removeItem(SESSION_ID_KEY);
    } catch {
        /* silent */
    }
}

// Exported for tests / future detection consumers.
export const __internals = {
    weightForSignal,
    labelForRisk,
    STORAGE_KEY,
    STYLE_CHANGE_KEY,
    LAST_DRIFT_KEY,
};
